import {ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NgMultiLabelTemplateDirective, NgSelectComponent} from '@ng-select/ng-select';
import {BaseComponent, FeatureTypes, FeatureValue, MultipleSelectValidatorContent, TemplateFeature} from 'compob2b-libs';
import {distinctUntilChanged, map, takeUntil} from 'rxjs/operators';
import {
  TemplateFeatureService,
  TemplateFeatureValue
} from '../../../../../../compob2b-admin/src/app/cabinet/pim/product/create-update/features/template-feature.service'
import {SlicePipe} from '@angular/common';
import {NbButtonModule, NbTooltipModule} from '@nebular/theme';
import {RouterLink} from '@angular/router';
import {TuiIcon} from '@taiga-ui/core'

@Component({
  selector: 'app-tf-select',
  templateUrl: './tf-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbButtonModule, NbTooltipModule, FormsModule, ReactiveFormsModule, NgSelectComponent, NgMultiLabelTemplateDirective, RouterLink, SlicePipe, TuiIcon]
})
export class TfSelectComponent extends BaseComponent implements OnInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;
  @Input() showRemoveButton = false;
  @Output() removeEvent = new EventEmitter<void>();

  loading = true;

  form = new UntypedFormGroup({
    featureValue: new UntypedFormControl(null)
  });

  featureValues: FeatureValue[] = [];
  private featureValuesCount = 0;

  showItemsCount = this.environment.visibleSelectTagCount;

  isValidatorSet = false;
  validatorName: string = null;
  multipleSelectValidator: MultipleSelectValidatorContent = null;

  templateFeatureService = inject(TemplateFeatureService);

  ngOnInit(): void {
    this.setValidator();

    this.form.get('featureValue').valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe(data => {
        this.setFeature(data, this.featureValue.valid, this.isShowEmptyWarning());
        this.cdr.detectChanges();
      });

    const uri = `/feature-value/feature/${this.feature.id}/template/${this.feature.templateId}`;
    this.compoApiService.get<FeatureValue[]>(uri)
      .pipe(
        takeUntil(this.unsubscribe),
        map(response => this.compoApiService.naturalSort(response.data, 'value')),
      )
      .subscribe(options => {
        // Seed the product's actually-saved values (incl. PIM2-580 defaults) into the items so
        // ng-select can resolve their labels. The per-template-feature options endpoint may NOT
        // return values the product holds (e.g. defaults pointing outside the current option scope);
        // their labels come from /template/product. Set the model only after items exist.
        const selected = this.templateFeatureService.getProductFeatureValues(this.feature.id);
        const optionIds = new Set(options.map(o => o.id));
        const seeded = selected
          .filter(v => !optionIds.has(v.id))
          .map(v => ({id: v.id, value: v.value} as FeatureValue));
        this.featureValues = seeded.length ? [...seeded, ...options] : options;
        this.featureValuesCount = this.featureValues.length;
        this.loading = false;
        this.setDefaultValue();
        this.cdr.detectChanges();
      });

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue();
        this.cdr.detectChanges();
      });
  }

  private setValidator(): void {
    if (this.feature.validator !== null && this.isMultiple()) {
      this.isValidatorSet = true;
      this.validatorName = this.feature.validator.header;
      this.multipleSelectValidator = this.feature.validator.content.multipleSelectValidator;

      const validators = [];
      if (this.multipleSelectValidator.minValuesNumber !== null) {
        validators.push(Validators.minLength(this.multipleSelectValidator.minValuesNumber));
      }

      if (this.multipleSelectValidator.maxValuesNumber !== null) {
        validators.push(Validators.maxLength(this.multipleSelectValidator.maxValuesNumber));
      }

      if (validators.length > 0) {
        this.featureValue.addValidators(validators);
      }
    }
  }

  private setDefaultValue(): void {
    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel) {
      this.featureValue.setValue(featureModel[this.feature.id], {emitEvent: false});
    }

    this.setDefaultFeatureValue(this.featureValue.value);
  }

  private setFeature(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty
    );
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      this.form.valid,
      this.isShowEmptyWarning()
    );
  }

  get featureValue(): AbstractControl {
    return this.form.get('featureValue');
  }

  isEmpty(): boolean {
    return this.featureValue.value === null || this.featureValue.value.length === 0;
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isEmpty();
  }

  isMultiple(): boolean {
    return FeatureTypes.CHECKBOX === this.feature.featureTypeId
      || (FeatureTypes.SELECT === this.feature.featureTypeId && this.feature.multiple === true);
  }

  showAllSelectedValues(select: NgSelectComponent) {
    this.showItemsCount = this.featureValuesCount;
    select.focus();
  }

  hideExtraSelectedValues() {
    this.showItemsCount = this.environment.visibleSelectTagCount;
  }

  showHideButton(selectedValueCount: number): boolean {
    return this.isMultiple()
      && this.featureValuesCount === this.showItemsCount && selectedValueCount > this.environment.visibleSelectTagCount;
  }

  showAllSelectedValuesButton(selectedValueCount: number): boolean {
    return this.isMultiple() && selectedValueCount > this.showItemsCount;
  }

  getHiddenSelectedItemsCount(selectedValueCount: number): number {
    return selectedValueCount - this.environment.visibleSelectTagCount;
  }

  onBlur($event: any) {
    this.hideExtraSelectedValues();
  }
}
